import { CalendarClock, ChevronRight, Gavel, Hourglass, LayoutGrid, List, Scale } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';
import { useSearchParams } from '@/lib/scopedParams';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';
import { api } from '../../../api';
import type { Auction, AuctionKind, AuctionStatus } from '../../../api/areas/market';
import { Badge } from '../../../components/Badge';
import { Callout, DataTable, Field, FilterBar, Section, StatCard, StatGrid, type Column } from '../../../components/blocks';
import { DataError } from '../../../components/DataError';
import { PageHeader } from '../../../components/PageHeader';
import { AuctionLinks } from '../module';
import { EmptyState, Loading } from '../../../components/States';
import { formatCount, formatDateTime, formatRelative } from '../../../lib/format';
import { useApi } from '../../../lib/useApi';
import { FilterSelect, RefreshButton, ResetButton } from '../../registry/shared';
import { AUCTION_STATUS, CLOSING_SOON_MS, Countdown, CustomerRef, decimalGte, isOver, LiveDot, MarketBadge, marketEnum, money, useNow } from '../shared';

const KINDS: AuctionKind[] = ['PUBLIC', 'BACKORDER_PRIVATE'];

function isClosingSoon(a: Auction, now: number): boolean {
  if (a.status !== 'OPEN') return false;
  const left = new Date(a.ends_at).getTime() - now;
  return left > 0 && left <= CLOSING_SOON_MS;
}

/** One session as a card (reference 64): kind, visibility, name, rule line, price, bids and time. */
function AuctionCard({ a, now }: { a: Auction; now: number }) {
  const { t } = useTranslation('market');
  const soon = isClosingSoon(a, now);
  const price = a.final_price ?? a.current_price ?? a.start_price;
  const to = `/auctions/${encodeURIComponent(a.id)}`;
  return (
    <li className={cn('flex min-w-0 flex-col rounded-xl border bg-card p-5', a.status === 'OPEN' && 'border-success/40', soon && 'border-warning/50')}>
      <div className="flex items-start justify-between gap-2">
        <span className="text-[10px] font-semibold tracking-[0.12em] text-muted-foreground uppercase">{marketEnum('auctionKind', a.kind)}</span>
        <MarketBadge group="auctionStatus" map={AUCTION_STATUS} value={a.status} />
      </div>
      <Link to={to} className="mt-3 inline-flex items-center gap-2 text-xl font-semibold tracking-tight break-all hover:underline">
        {a.status === 'OPEN' ? <LiveDot /> : null}
        {a.domain_name}
      </Link>
      <p className="mt-1 text-[13px] text-muted-foreground">
        {a.kind === 'PUBLIC' ? t('auction.public') : t('auction.private')}
        {a.reserve_price !== null ? ` · ${a.current_price !== null && decimalGte(a.current_price, a.reserve_price) ? t('auctions.reserveMet') : t('au.market.reserveNotMet')}` : ''}
      </p>
      <div className="tabular mt-4 text-2xl font-semibold text-primary">{money(price, a.currency)}</div>
      <div className="mt-auto flex flex-wrap items-center justify-between gap-2 pt-4">
        <span className="text-xs text-muted-foreground">
          {t('auctions.bidCount', { count: a.bid_count })} ·{' '}
          {a.status === 'OPEN' ? (
            <Countdown to={a.ends_at} now={now} prefix={false} className={cn(soon && 'font-semibold text-warning')} />
          ) : a.status === 'SCHEDULED' ? (
            <Countdown to={a.starts_at} now={now} mode="starts" />
          ) : (
            <span title={formatDateTime(a.ends_at)}>{formatRelative(a.ends_at)}</span>
          )}
        </span>
        <Button asChild variant="outline" size="sm">
          <Link to={to} aria-label={t('auctions.open', { domain: a.domain_name })}>
            <ChevronRight /> {t('au.market.openSession')}
          </Link>
        </Button>
      </div>
    </li>
  );
}

/** `/auctions` (tab Phiên đấu giá), reference 64: every session as a card (or a table), live ones first to the eye. */
export function AuctionMarketPage() {
  const { t } = useTranslation('market');
  const [params, setParams] = useSearchParams();
  const status = (params.get('status') ?? '') as AuctionStatus | '';
  const kind = (params.get('kind') ?? '') as AuctionKind | '';
  const domain = params.get('domain') ?? '';
  const view = params.get('view') === 'table' ? 'table' : 'cards';
  const result = useApi(
    (signal) => api.listAuctions({ status: status || undefined, kind: kind || undefined, domain: domain || undefined, limit: 200 }, { signal }),
    [status, kind, domain],
  );
  const overview = useApi((signal) => api.listAuctions({ limit: 200 }, { signal }), []);
  const now = useNow(1000);
  const set = (k: string, v: string) => {
    const p = new URLSearchParams(params);
    if (v) p.set(k, v);
    else p.delete(k);
    setParams(p);
  };
  const reload = () => {
    result.reload();
    overview.reload();
  };

  const all = overview.status === 'success' ? overview.data.items : [];
  const live = all.filter((a) => a.status === 'OPEN');
  const closingSoon = live.filter((a) => isClosingSoon(a, now));
  const awaiting = all.filter((a) => a.status === 'CLOSED');
  const scheduled = all.filter((a) => a.status === 'SCHEDULED');

  const columns: Column<Auction>[] = [
    {
      key: 'domain',
      header: t('auctions.domain'),
      cell: (a) => (
        <span className="inline-flex items-start gap-2">
          {a.status === 'OPEN' ? <LiveDot className="mt-2" /> : null}
          <span className="space-y-0.5">
            <Link to={`/auctions/${encodeURIComponent(a.id)}`} className="block font-semibold text-primary hover:underline">
              {a.domain_name}
            </Link>
            <span className="block text-xs text-muted-foreground" title={a.kind}>
              {marketEnum('auctionKind', a.kind)}
            </span>
          </span>
        </span>
      ),
    },
    {
      key: 'status',
      header: t('auctions.status'),
      cell: (a) => (
        <span className="flex flex-wrap gap-1">
          <MarketBadge group="auctionStatus" map={AUCTION_STATUS} value={a.status} />
          {isClosingSoon(a, now) ? <Badge tone="warn">{t('countdown.closingSoon')}</Badge> : null}
        </span>
      ),
    },
    {
      key: 'leader',
      header: t('auctions.leader'),
      className: 'max-w-44 whitespace-normal',
      cell: (a) =>
        a.winner_customer_id ? (
          <CustomerRef id={a.winner_customer_id} name={a.winner_name} sub={t('auction.bidWinner')} />
        ) : a.highest_bidder_id ? (
          <CustomerRef id={a.highest_bidder_id} name={a.leader_name} sub={isOver(a.status) ? t('auction.bidHighest') : t('auction.bidLeading')} />
        ) : (
          <span className="text-muted-foreground">{t('auctions.noBidder')}</span>
        ),
    },
    { key: 'price', header: t('auctions.price'), numeric: true, className: 'max-w-32 whitespace-normal', cell: (a) => (
        <div className="space-y-0.5">
          <div className="font-medium whitespace-nowrap">{money(a.final_price ?? a.current_price ?? a.start_price, a.currency)}</div>
          <div className="text-xs text-muted-foreground">{t('auctions.bidCount', { count: a.bid_count })}</div>
        </div>
      ),
    },
    {
      key: 'reserve',
      header: t('auctions.reserve'),
      numeric: true,
      cell: (a) =>
        a.reserve_price === null ? (
          <span className="text-muted-foreground">{t('none')}</span>
        ) : (
          <span className="inline-flex flex-wrap items-center justify-end gap-x-1.5">
            <span className="whitespace-nowrap">{money(a.reserve_price, a.currency)}</span>
            {a.current_price !== null && decimalGte(a.current_price, a.reserve_price) ? <Badge tone="ok">{t('auctions.reserveMet')}</Badge> : null}
          </span>
        ),
    },
    {
      key: 'ends',
      header: t('auctions.ends'),
      cell: (a) => (
        <div className="space-y-0.5 text-sm">
          {a.status === 'OPEN' ? (
            <Countdown to={a.ends_at} now={now} prefix={false} className={cn('font-semibold', isClosingSoon(a, now) && 'text-warning')} />
          ) : a.status === 'SCHEDULED' ? (
            <Countdown to={a.starts_at} now={now} mode="starts" />
          ) : (
            <span className="whitespace-nowrap" title={formatDateTime(a.ends_at)}>
              {formatRelative(a.ends_at)}
            </span>
          )}
          {a.extension_count > 0 ? <div className="text-xs text-muted-foreground">{t('auctions.extended', { count: a.extension_count })}</div> : null}
        </div>
      ),
    },
    {
      key: 'go',
      header: <span className="sr-only">{t('auctions.open', { domain: '' })}</span>,
      className: 'w-8',
      cell: (a) => (
        <Button asChild variant="ghost" size="icon-xs">
          <Link to={`/auctions/${encodeURIComponent(a.id)}`} aria-label={t('auctions.open', { domain: a.domain_name })}>
            <ChevronRight />
          </Link>
        </Button>
      ),
    },
  ];

  return (
    <>
      <PageHeader title={t('au.market.title')} subtitle={t('auctions.subtitle')} actions={<RefreshButton onClick={reload} />} />
      <AuctionLinks here="market" />
      <div className="space-y-6">
        {overview.status === 'success' ? (
          <section aria-label={t('auctions.title')} className="space-y-2">
            <StatGrid>
              <StatCard label={t('auctions.live')} icon={Gavel} value={formatCount(live.length)} tone={live.length ? 'ok' : 'neutral'} hint={t('auctions.liveHint')} to="/auctions?status=OPEN" linkLabel={t('auctions.showThese')} />
              <StatCard
                label={t('auctions.closingSoon', { hours: CLOSING_SOON_MS / 3_600_000 })}
                icon={Hourglass}
                value={formatCount(closingSoon.length)}
                tone={closingSoon.length ? 'warn' : 'neutral'}
                hint={t('auctions.closingSoonHint')}
              >
                {closingSoon.slice(0, 3).map((a) => (
                  <Link key={a.id} to={`/auctions/${encodeURIComponent(a.id)}`} className="flex items-center justify-between gap-2 text-foreground hover:underline">
                    <span className="truncate font-medium">{a.domain_name}</span>
                    <Countdown to={a.ends_at} now={now} prefix={false} className="text-xs font-semibold text-warning" />
                  </Link>
                ))}
              </StatCard>
              <StatCard
                label={t('auctions.awaiting')}
                icon={Scale}
                value={formatCount(awaiting.length)}
                tone={awaiting.length ? 'pending' : 'neutral'}
                hint={t('auctions.awaitingHint')}
                to="/auctions?status=CLOSED"
                linkLabel={t('auctions.showThese')}
              />
              <StatCard
                label={t('auctions.scheduled')}
                icon={CalendarClock}
                value={formatCount(scheduled.length)}
                hint={t('auctions.scheduledHint')}
                to="/auctions?status=SCHEDULED"
                linkLabel={t('auctions.showThese')}
              />
            </StatGrid>
          </section>
        ) : null}

        <div>
          <FilterBar actions={<ResetButton onClick={() => setParams(new URLSearchParams(view === 'table' ? { view } : {}))} disabled={!status && !kind && !domain} />}>
            <FilterSelect
              label={t('auctions.status')}
              value={status}
              onChange={(v) => set('status', v)}
              anyLabel={t('any')}
              className="min-w-48"
              options={(Object.keys(AUCTION_STATUS) as AuctionStatus[]).map((s) => ({ value: s, label: marketEnum('auctionStatus', s) }))}
            />
            <Field label={t('auctions.kind')}>
              <div role="group" aria-label={t('auctions.kind')} className="inline-flex h-8 rounded-md border p-0.5">
                {(['', ...KINDS] as const).map((k) => (
                  <button
                    key={k || 'all'}
                    type="button"
                    aria-pressed={kind === k}
                    onClick={() => set('kind', k)}
                    className={cn('rounded px-3 text-[13px] font-medium whitespace-nowrap', kind === k ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:text-foreground')}
                  >
                    {k ? marketEnum('auctionKind', k) : t('au.market.all')}
                  </button>
                ))}
              </div>
            </Field>
            <Field label={t('auctions.domain')} className="min-w-48">
              <Input
                key={domain}
                type="search"
                className="h-8"
                defaultValue={domain}
                onKeyDown={(e) => e.key === 'Enter' && set('domain', e.currentTarget.value.trim())}
                onBlur={(e) => e.target.value.trim() !== domain && set('domain', e.target.value.trim())}
                placeholder={t('auctions.domainPlaceholder')}
              />
            </Field>
            <Field label={t('au.market.view')}>
              <div role="group" aria-label={t('au.market.view')} className="inline-flex h-8 rounded-md border p-0.5">
                {(['cards', 'table'] as const).map((v) => (
                  <button
                    key={v}
                    type="button"
                    aria-pressed={view === v}
                    aria-label={t(`au.market.${v}`)}
                    title={t(`au.market.${v}`)}
                    onClick={() => set('view', v === 'table' ? 'table' : '')}
                    className={cn('rounded px-2', view === v ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:text-foreground')}
                  >
                    {v === 'cards' ? <LayoutGrid className="size-4" /> : <List className="size-4" />}
                  </button>
                ))}
              </div>
            </Field>
          </FilterBar>
          {view === 'cards' ? (
            <section aria-label={t('auctions.list')}>
              {result.status === 'loading' ? <Loading rows={4} /> : null}
              {result.status === 'error' ? <DataError error={result.error} onRetry={result.reload} title={t('auctions.loadError')} /> : null}
              {result.status === 'success' ? (
                result.data.items.length ? (
                  <ul className="grid grid-cols-1 gap-4 md:grid-cols-2 2xl:grid-cols-3">
                    {result.data.items.map((a) => (
                      <AuctionCard key={a.id} a={a} now={now} />
                    ))}
                  </ul>
                ) : (
                  <EmptyState title={t('auctions.empty')} />
                )
              ) : null}
            </section>
          ) : (
          <Section flush title={t('auctions.list')} description={t('auctions.listDesc')}>
            {result.status === 'loading' ? (
              <div className="px-5">
                <Loading rows={5} />
              </div>
            ) : null}
            {result.status === 'error' ? (
              <div className="px-5 pb-5">
                <DataError error={result.error} onRetry={result.reload} title={t('auctions.loadError')} />
              </div>
            ) : null}
            {result.status === 'success' ? <DataTable columns={columns} rows={result.data.items} rowKey={(a) => a.id} empty={<EmptyState title={t('auctions.empty')} />} /> : null}
          </Section>
          )}
        </div>
        <Callout tone="info" title={t('au.market.rulesTitle')}>
          {t('au.market.rules')}
        </Callout>
      </div>
    </>
  );
}
